import rfdc from "rfdc";
import { hasContents } from "../node/document-helper";
import type { BaseNode, IDocumentData } from "../node/type";
import { getUnitStyle, jsonifyDocumentNode, removeByKeys } from "../node/util";
import type {
  Campaign,
  CampaignType,
  CampaignWithRel,
  EmptyScheduleStrategy,
  IMeta,
  SlotMeta,
} from "../types/campaign";
import type { ExternalSiteType } from "../types/site";
import { createPopupCampaignData } from "./create";
import { isLowerVersionThan } from "./document-data-version";
import { exhaustiveGuardWithDefaultValue } from "./exhaustive-guard";
import { getSwiperContainerNodefromDocument } from "./form";
import invariant from "./invariant";
import type { Keys } from "./types";
import { removeUndefinedOrNullField } from "./value";
const deepclone = rfdc();

export const jsonifyCampaignData = (
  campaignData: IDocumentData,
  options?: { excludeKeys?: Array<Keys<BaseNode>> }
) => {
  return {
    ...deepclone(removeByKeys(campaignData, ["documents"])),
    documents: campaignData.documents.map((campaign) =>
      jsonifyDocumentNode(campaign, options)
    ),
  };
};

export const duplicatePopupCampaignData = (
  campaignData: IDocumentData
): IDocumentData => {
  return {
    ...createPopupCampaignData(
      jsonifyCampaignData(campaignData, {
        excludeKeys: ["id", "templates"],
      })
    ),
  };
};

export const getCampaignName = (campaignType: CampaignType | "ALL"): string => {
  switch (campaignType) {
    case "POPUP":
      return "팝업";
    case "INSTAGRAM":
    case "INSTAGRAM_V2":
      return "인스타그램";
    case "BANNER":
      return "배너";
    case "BANNER_GROUP":
      return "배너 영역";
    case "POPUP_GROUP":
      return "팝업 영역";
    case "FORM_EVENT":
      return "이벤트";
    case "PROMOTION":
      return "이벤트 페이지";
    case "FORM_SURVEY":
      return "설문조사";
    case "COLLECTION":
      return "컬렉션";
    case "CHILD_EVENT_PAGE":
      return "이벤트 페이지";
    case "MESSAGE":
      return "메시지";
    case "ALL":
      return "전체";
  }
};

export const getI18nCampaignName = (campaignType: CampaignType | "ALL") => {
  switch (campaignType) {
    case "POPUP":
      return "popup";
    case "INSTAGRAM":
    case "INSTAGRAM_V2":
      return "instagram";
    case "BANNER":
      return "banner";
    case "BANNER_GROUP":
      return "bannerGroup";
    case "POPUP_GROUP":
      return "groupPopup";
    case "FORM_EVENT":
      return "formEvent";
    case "PROMOTION":
      return "promotion";
    case "FORM_SURVEY":
      return "survey";
    case "COLLECTION":
      return "collection";
    case "CHILD_EVENT_PAGE":
      return "childEventPage";
    case "ALL":
      return "all";
    case "MESSAGE":
      return "message";
    default:
      return exhaustiveGuardWithDefaultValue(campaignType, "unknown");
  }
};

export const getSlotMetaData = (meta?: IMeta): SlotMeta[] => {
  if (!meta || !meta.slot) {
    return [
      { device: "MOBILE", slot: 5 },
      { device: "DESKTOP", slot: 5 },
      { device: "ALL", slot: 5 },
    ];
  }

  return meta.slot;
};

export const getMetaOnlyHtmlSuffix = (
  campaignType: Extract<
    CampaignType,
    { type: `FORM_${string}` | "PROMOTION" }["type"]
  >
) => {
  const filenameSuffix: Record<typeof campaignType, string> = {
    FORM_EVENT: "event-og",
    PROMOTION: "promotion-og",
    FORM_SURVEY: "survey-og",
  };

  return filenameSuffix[campaignType];
};

export const isFullPageCampaign = (
  campaign: Campaign
): campaign is Extract<Campaign, { type: `FORM_${string}` | "PROMOTION" }> => {
  return (
    campaign.type === "PROMOTION" ||
    campaign.type === "FORM_EVENT" ||
    campaign.type === "FORM_SURVEY" ||
    campaign.type === "CHILD_EVENT_PAGE" ||
    campaign.type === "COLLECTION"
  );
};

export const getNoContents = (campaign: CampaignWithRel): boolean => {
  const hasDataSource =
    campaign.dataSources &&
    campaign.dataSources[0]?.sourceType === "CODENBUTTER_INSTAGRAM" &&
    campaign.dataSources[0]?.sourceParam.source.userId !== "";

  const isInstagramProfessionalNone =
    campaign.dataSources &&
    campaign.dataSources[0]?.sourceType === "CODENBUTTER_INSTAGRAM" &&
    campaign.dataSources[0]?.sourceParam.source.type === "PROFESSIONAL_NONE";

  switch (campaign.type) {
    case "INSTAGRAM":
      return true;
    case "INSTAGRAM_V2":
      return !hasDataSource || isInstagramProfessionalNone;
    case "POPUP":
    case "BANNER":
    case "BANNER_GROUP":
    case "POPUP_GROUP":
    case "FORM_EVENT":
    case "PROMOTION":
    case "FORM_SURVEY":
    case "COLLECTION":
    case "CHILD_EVENT_PAGE":
      return !hasContents(campaign.data);
  }
};

export const isChildPopup = (campaign: Campaign) => {
  return campaign.type === "POPUP" && Boolean(campaign.parentPublicId);
};

export const getEmptyTargetStrategy = (
  campaign: Campaign
): EmptyScheduleStrategy => {
  return campaign.type === "BANNER" || isChildPopup(campaign)
    ? "noSchedule"
    : "always";
};

export const isLowerVersionBannerGroup = (campaign: Campaign) => {
  return (
    campaign.type === "BANNER_GROUP" &&
    isLowerVersionThan(campaign.data, "0.0.2")
  );
};

export const isLowerVersionPopupGroup = (campaign: Campaign) => {
  return (
    campaign.type === "POPUP_GROUP" &&
    isLowerVersionThan(campaign.data, "0.0.2")
  );
};

export const isLowerVersionPopup = (campaign: Campaign) => {
  return (
    campaign.type === "POPUP" && isLowerVersionThan(campaign.data, "0.0.2")
  );
};

export const isLowerVersionPromotion = (campaign: Campaign) => {
  return (
    campaign.type === "PROMOTION" && isLowerVersionThan(campaign.data, "0.0.2")
  );
};

export const isLowerVersionChildEventPage = (campaign: Campaign) => {
  return (
    campaign.type === "CHILD_EVENT_PAGE" &&
    isLowerVersionThan(campaign.data, "0.0.2")
  );
};

const records: Record<ExternalSiteType, ExternalSiteType | undefined> = {
  OOPY: "OOPY",
  SIXSHOP: "SIXSHOP",
  MAKESHOP: "MAKESHOP",
  IMWEB: "IMWEB",
  CAFE24: "CAFE24",
  SHOPIFY: "SHOPIFY",
  WIX: "WIX",
  SHOPBY: "SHOPBY",
  SHOPBY_PRO: "SHOPBY_PRO",
  GODOMALL: "GODOMALL",
  NAVER_SMARTSTORE: "NAVER_SMARTSTORE",
  WORDPRESS: "WORDPRESS",
  SIXSHOP_PRO: "SIXSHOP_PRO",
};

export const stringToExternalSiteType = (
  type?: unknown
): ExternalSiteType | null => {
  if (type === null || type === undefined) {
    return null;
  }
  const externalSiteType = records[type as ExternalSiteType];
  invariant(
    !!externalSiteType,
    "wrong external site type: " + externalSiteType
  );
  return externalSiteType;
};

export const filterUnnecessaryMeta = (
  campaignType: CampaignType,
  meta?: IMeta
): IMeta => {
  switch (campaignType) {
    case "PROMOTION":
      return removeUndefinedOrNullField({
        area: meta?.area,
        deviceArea: meta?.deviceArea,
        pageInfo: meta?.pageInfo,
        og: meta?.og,
      });
    case "FORM_EVENT":
    case "FORM_SURVEY":
      return removeUndefinedOrNullField({
        area: meta?.area,
        og: meta?.og,
      });
    case "BANNER_GROUP":
    case "POPUP_GROUP":
    case "POPUP":
    case "BANNER":
      return removeUndefinedOrNullField({
        area: meta?.area,
        slot: meta?.slot,
      });
    case "COLLECTION":
      return {
        area: meta?.area,
        deviceArea: meta?.deviceArea,
        childrenOrder: meta?.childrenOrder,
      };
    case "CHILD_EVENT_PAGE":
      return removeUndefinedOrNullField({
        area: meta?.area,
        deviceArea: meta?.deviceArea,
        pageInfo: meta?.pageInfo,
      });
    case "INSTAGRAM":
    case "INSTAGRAM_V2":
    case "MESSAGE":
      return meta ?? {};
  }
};

export const generateEmbeddedSyncScript = (campaign: Campaign) => {
  const baseSrc = process.env.S3_CONFIG_BUCKET;

  if (campaign.type !== "BANNER_GROUP") {
    throw new Error("Instagram campaign does not support script");
  }
  const campaignType = "banner-group";

  const document = campaign.data.documents[0];
  if (!document) {
    throw new Error("document is not found");
  }
  const swiperContainer = getSwiperContainerNodefromDocument(document);
  if (!swiperContainer) {
    throw new Error("swiperContainer is not found");
  }
  const device = document.device;
  const campaignPublicId = campaign.publicId;
  const stylePrefix =
    document.height?.unit === "px"
      ? `height: calc(${getUnitStyle(document.height)} + `
      : swiperContainer.height?.unit === "px"
      ? `height: calc(${getUnitStyle(swiperContainer.height)} + `
      : swiperContainer.aspectRatio?.type === "MANUAL"
      ? `padding-bottom: calc(${
          ((swiperContainer.aspectRatio.heightRatio || 1) * 100) /
          (swiperContainer.aspectRatio.widthRatio || 1)
        }% + `
      : "";

  /**
   * 1. curretentScript가 존재하는지 확인
   * 2. curretentScript가 SVG 스크립트인지 확인
   * 3. curretentScript.src가 코드앤버터 s3 버킷이 아닌지 혹은 curretentScript가 head에 있는지 확인
   *  3-1 true라면, querySelector로 body에 있는 스크립트 찾기
   * 4. 스크립트 src의 searchParams 혹은 data attirbute에서 placeholder가 true인지 확인, device확인
   * 5. placeholder, device에 따라 style 설정
   * 6. 스크립트가 async, defer, document.readyState가 complete, interactive, loaded인지 확인
   *  6-1 true라면, insertAdjacentHTML로 스크립트 뒤에 cbElement 추가
   *  6-2 false라면, document.write로 cbElement 추가
   * 7. 현재 스크립트에 data-cb-executed 속성 추가
   */
  return /* js */ `(() => {
  let script = document.currentScript;
  if (!script) { console.warn("No current script found"); return; }
  if (script instanceof SVGScriptElement) { console.warn("cannot execute in SVGScriptElement"); return; }
  if (!script.src.includes("${baseSrc}") || script.closest('head')) { script = document.querySelector('body script:not([data-cb-executed])[src*="/${campaign.publicId}.js"]'); }
  if (!script) { console.warn("No CB script found"); return; }
  function getOs() {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    if (/windows phone/i.test(userAgent)) { return "Windows Phone"; }
    if (/android/i.test(userAgent)) { return "Android"; }
    if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) { return "iOS"; }
    return "Desktop";
  }
  function getDevice() {
    if (getOs() !== "Desktop") { return "MOBILE"; }
    if (window.innerWidth > 100 && window.innerWidth < 430) { return "MOBILE"; }
    return "DESKTOP";
  }
  const searchParams = new URLSearchParams(script.src.split("?")[1]);
  const extraHeight = Number(script.getAttribute("data-cb-extra-height") || 0);
  const sanitizedHeight = Number.isNaN(extraHeight) ? 0 : extraHeight;
  const useFadeIn = script.getAttribute("data-cb-fadein") || false;
  const usePlaceholder = (script.getAttribute("data-cb-placeholder") || searchParams.get("placeholder") === "true") && (getDevice() === "${device}" || "${device}" === "ALL");
  const cbElement = usePlaceholder ? \`<div data-cb-embedded="${campaignType}" data-campaign-id="${campaignPublicId}" style='\${useFadeIn ? "opacity: 0; transition: all 0.3s;" : ""}'><div data-cb-placeholder="true" style="${stylePrefix} \${sanitizedHeight}px);"></div></div>\` : '<div data-cb-embedded="${campaignType}" data-campaign-id="${campaignPublicId}"></div>'
  if (script.async || script.defer || /complete|interactive|loaded/.test(document.readyState)) { script.insertAdjacentHTML('afterend', cbElement);}
  else { document.write(cbElement); }
  script.setAttribute("data-cb-executed", "true");
})();`;
};
